import type { RaRecord } from "react-admin";

import {
  formatNarrationIts,
  formatNarrationRupee,
  formatNarrationTransactionId,
} from "./receipt-narration-format";

export type SabilReceiptNarrationInput = {
  amount?: unknown;
  paymentMode?: unknown;
  transactionId?: unknown;
  paymentRef?: unknown;
  takhmeenAmount?: unknown;
  paidMonths?: unknown;
  /** When set (e.g. resolved HOF ITS), used instead of sabilData.itsNo */
  itsNo?: string;
  sabilData?: {
    itsNo?: string;
    name?: string;
    firmName?: string;
    sabilType?: string;
    itsdata?: {
      Full_Name?: string;
      ITS_ID?: string;
      HOF_ID?: string | null;
      HOF_FM_TYPE?: string | null;
    };
  } | null;
};

function parseAmount(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

/** Same source as SabilReceiptPrint “HOF ITS”: sabil.itsNo, then directory HOF/ITS. */
export function resolveSabilHofIts(sabilData: SabilReceiptNarrationInput["sabilData"]): string {
  const itsNo = sabilData?.itsNo?.trim();
  if (itsNo) return itsNo;

  const its = sabilData?.itsdata;
  if (!its) return "";

  if (its.HOF_FM_TYPE === "FM" && its.HOF_ID?.trim()) {
    return its.HOF_ID.trim();
  }
  return its.ITS_ID?.trim() || its.HOF_ID?.trim() || "";
}

export type SabilReceiptNarrationOptions = {
  includeItsNo?: boolean;
  includeAmount?: boolean;
  includePaymentMode?: boolean;
  includeTransactionId?: boolean;
  includeName?: boolean;
  includeTakhmeen?: boolean;
};

/** ITS, paid amount, payment mode, txn id, name, takhmeen × months — Tally export (not printed on receipt). */
export function formatSabilReceiptNarration(
  input: SabilReceiptNarrationInput,
  options?: SabilReceiptNarrationOptions
): string {
  const includeItsNo = options?.includeItsNo ?? true;
  const includeAmount = options?.includeAmount ?? true;
  const includePaymentMode = options?.includePaymentMode ?? true;
  const includeTransactionId = options?.includeTransactionId ?? true;
  const includeName = options?.includeName ?? true;
  const includeTakhmeen = options?.includeTakhmeen ?? true;

  const sabilData = input.sabilData ?? undefined;
  const itsPart = includeItsNo
    ? formatNarrationIts(input.itsNo ?? resolveSabilHofIts(sabilData))
    : "";
  const paidAmountNum = parseAmount(input.amount);
  const paidAmount = includeAmount ? formatNarrationRupee(paidAmountNum) : "";
  const paymentMode = includePaymentMode
    ? String(input.paymentMode ?? "")
        .trim()
        .toUpperCase()
    : "";
  const txnPart = includeTransactionId
    ? formatNarrationTransactionId(input.transactionId ?? input.paymentRef)
    : "";
  const isEstablishment = sabilData?.sabilType === "ESTABLISHMENT";
  const name = includeName
    ? (isEstablishment ? sabilData?.firmName || sabilData?.name : sabilData?.name)?.trim() ||
      sabilData?.itsdata?.Full_Name?.trim() ||
      ""
    : "";

  let takhmeenPart = "";
  if (includeTakhmeen) {
    const takhmeenAmountNum = parseAmount(input.takhmeenAmount);
    let months = parseAmount(input.paidMonths);
    if (months <= 0 && takhmeenAmountNum > 0 && paidAmountNum > 0) {
      months = Math.round(paidAmountNum / takhmeenAmountNum);
    }
    const takhmeenRupee = formatNarrationRupee(takhmeenAmountNum);
    takhmeenPart =
      takhmeenAmountNum > 0 && months > 0 ? `${takhmeenRupee} * ${months}` : takhmeenRupee;
  }

  return [itsPart, paidAmount, paymentMode, txnPart, name, takhmeenPart]
    .filter((part) => part !== "")
    .join(", ");
}

export function formatSabilReceiptNarrationFromRecord(
  rec: RaRecord,
  options?: SabilReceiptNarrationOptions
): string {
  return formatSabilReceiptNarration(
    {
      amount: rec.amount,
      paymentMode: rec.paymentMode,
      transactionId: rec.transactionId,
      paymentRef: rec.paymentRef,
      takhmeenAmount: rec.takhmeenAmount,
      paidMonths: rec.paidMonths,
      sabilData: rec.sabilData as SabilReceiptNarrationInput["sabilData"],
    },
    options
  );
}
